/*
 * Settings → Notifications & escalation (NotificationSettingController), as
 * the real API answers it. The catalogue is the API's own
 * (api/config/notification-events.php), copied here so the prototype offers
 * the same events, templates and merge fields; changes go into the sample
 * change log like every other settings screen.
 */
import { ok, fail, invalid, notFound } from "./util";
import { recordChange } from "@/mock/settings";
import { roles } from "./access";

const CATALOGUE = {
  "events": {
    "task.assigned": {
      "label": "Task assigned to you",
      "group": "Work",
      "module": "tasks",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner"
      ],
      "template": "task_assigned",
      "tone": "info",
      "icon": "iconoir-check-circle"
    },
    "task.reassigned": {
      "label": "Task moved to somebody else",
      "group": "Work",
      "module": "tasks",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner",
        "previous_owner"
      ],
      "template": "task_assigned",
      "tone": "info",
      "icon": "iconoir-repeat"
    },
    "task.reminder": {
      "label": "Task reminder",
      "group": "Work",
      "module": "tasks",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner"
      ],
      "template": "task_reminder",
      "tone": "info",
      "icon": "iconoir-alarm",
      "notify_actor": true
    },
    "task.escalated": {
      "label": "Overdue task escalated",
      "group": "Work",
      "module": "tasks",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "escalated_to",
        "owner"
      ],
      "template": "task_escalated",
      "tone": "warning",
      "icon": "iconoir-arrow-up",
      "notify_actor": true
    },
    "task.completed": {
      "label": "A task you gave was done",
      "group": "Work",
      "module": "tasks",
      "channels": [
        "in_app"
      ],
      "recipients": [
        "assigned_by"
      ],
      "template": "task_completed",
      "tone": "info",
      "icon": "iconoir-check"
    },
    "record.assigned": {
      "label": "Record assigned to you",
      "group": "Records",
      "module": "records",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner"
      ],
      "template": "record_assigned",
      "tone": "info",
      "icon": "iconoir-user"
    },
    "record.stage_changed": {
      "label": "Your record moved stage",
      "group": "Records",
      "module": "records",
      "channels": [
        "in_app"
      ],
      "recipients": [
        "owner"
      ],
      "template": "stage_changed",
      "tone": "info",
      "icon": "iconoir-arrow-right"
    },
    "record.stage_ageing": {
      "label": "Record over the maximum days in its stage",
      "group": "Records",
      "module": "records",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner",
        "manager"
      ],
      "template": "stage_ageing",
      "tone": "warning",
      "icon": "iconoir-timer",
      "notify_actor": true
    },
    "record.revisit_due": {
      "label": "Recyclable record back for another look",
      "group": "Records",
      "module": "records",
      "channels": [
        "in_app"
      ],
      "recipients": [
        "owner"
      ],
      "template": "revisit_due",
      "tone": "info",
      "icon": "iconoir-refresh",
      "notify_actor": true
    },
    "record.escalated": {
      "label": "Record escalated to you",
      "group": "Records",
      "module": "records",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "escalated_to"
      ],
      "template": "record_escalated",
      "tone": "warning",
      "icon": "iconoir-arrow-up",
      "notify_actor": true
    },
    "approval.waiting": {
      "label": "Approval waiting for you",
      "group": "Approvals",
      "module": "approvals",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "approver"
      ],
      "template": "approval_pending",
      "tone": "warning",
      "icon": "iconoir-check-circle",
      "notify_actor": true
    },
    "approval.decided": {
      "label": "Your request was decided",
      "group": "Approvals",
      "module": "approvals",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "requester"
      ],
      "template": "approval_decided",
      "tone": "info",
      "icon": "iconoir-clipboard-check"
    },
    "ticket.assigned": {
      "label": "Ticket assigned to you",
      "group": "Support",
      "module": "tickets",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner"
      ],
      "template": "record_assigned",
      "tone": "info",
      "icon": "iconoir-headset-help"
    },
    "sla.at_risk": {
      "label": "SLA about to breach",
      "group": "Support",
      "module": "tickets",
      "channels": [
        "in_app"
      ],
      "recipients": [
        "owner"
      ],
      "template": "sla_breach",
      "tone": "warning",
      "icon": "iconoir-timer",
      "notify_actor": true
    },
    "sla.breached": {
      "label": "SLA breached",
      "group": "Support",
      "module": "tickets",
      "channels": [
        "in_app",
        "email",
        "sms"
      ],
      "recipients": [
        "owner",
        "team_lead"
      ],
      "template": "sla_breach",
      "tone": "warning",
      "icon": "iconoir-warning-triangle",
      "notify_actor": true
    },
    "renewal.due": {
      "label": "Renewal due (90/60/30/7 days)",
      "group": "Customer Success",
      "module": "renewals",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner",
        "role:finance"
      ],
      "template": "renewal_due",
      "tone": "info",
      "icon": "iconoir-refresh-double",
      "notify_actor": true
    },
    "churn.risk": {
      "label": "Churn risk raised",
      "group": "Customer Success",
      "module": "retention",
      "channels": [
        "in_app",
        "email"
      ],
      "recipients": [
        "owner",
        "manager"
      ],
      "template": "churn_alert",
      "tone": "warning",
      "icon": "iconoir-warning-triangle"
    }
  },
  "templates": {
    "task_assigned": {
      "name": "Task assigned",
      "subject": "{{task.type}}: {{task.title}}",
      "body": "Hi {{user.first_name}},\n\n{{actor.name}} has given you {{task.code}} \"{{task.title}}\", due {{task.due}} ({{task.priority}} priority).\n\n{{record.type}} {{record.name}}"
    },
    "task_reminder": {
      "name": "Task reminder",
      "subject": "Reminder: {{task.title}} is due {{task.due}}",
      "body": "Hi {{user.first_name}},\n\nThis is your reminder for {{task.code}} \"{{task.title}}\", due {{task.due}}.\n\n{{record.type}} {{record.name}}"
    },
    "task_escalated": {
      "name": "Overdue task escalated",
      "subject": "Overdue: {{task.title}} ({{owner.name}})",
      "body": "Hi {{user.first_name}},\n\n{{task.code}} \"{{task.title}}\" owned by {{owner.name}} was due {{task.due}} and is still open. It has been escalated to level {{escalation.level}} ({{escalation.to}}).\n\n{{record.type}} {{record.name}}"
    },
    "task_completed": {
      "name": "Task done",
      "subject": "Done: {{task.title}}",
      "body": "Hi {{user.first_name}},\n\n{{actor.name}} has completed {{task.code}} \"{{task.title}}\"."
    },
    "record_assigned": {
      "name": "Record assigned",
      "subject": "{{record.type}} {{record.code}} has been assigned to you",
      "body": "Hi {{user.first_name}},\n\n{{record.name}} is now yours, from {{actor.name}}."
    },
    "stage_changed": {
      "name": "Stage changed",
      "subject": "{{record.type}} {{record.code}} moved to {{stage.to}}",
      "body": "Hi {{user.first_name}},\n\n{{actor.name}} moved {{record.name}} from {{stage.from}} to {{stage.to}}."
    },
    "stage_ageing": {
      "name": "Stage ageing",
      "subject": "{{record.name}} has been in {{stage.to}} for {{stage.days}} days",
      "body": "Hi {{user.first_name}},\n\n{{record.type}} {{record.code}} has been in {{stage.to}} for {{stage.days}} days, over the {{stage.max_days}} allowed."
    },
    "revisit_due": {
      "name": "Revisit due",
      "subject": "Time to look at {{record.name}} again",
      "body": "Hi {{user.first_name}},\n\n{{record.type}} {{record.code}} was closed with {{outcome.reason}}. Its revisit date has come and it is back in the pool."
    },
    "record_escalated": {
      "name": "Record escalated",
      "subject": "Escalated to you: {{record.type}} {{record.code}}",
      "body": "Hi {{user.first_name}},\n\n{{record.name}} has been escalated to you (level {{escalation.level}}): {{escalation.reason}}."
    },
    "approval_pending": {
      "name": "Approval pending",
      "subject": "Approval needed: {{approval.title}}",
      "body": "Hi {{user.first_name}},\n\n{{requester.name}} has asked for {{approval.title}} ({{approval.reference}}). It is waiting for your decision at step {{approval.step}}."
    },
    "approval_decided": {
      "name": "Approval decided",
      "subject": "{{approval.title}} was {{approval.decision}}",
      "body": "Hi {{user.first_name}},\n\nYour request {{approval.reference}} ({{approval.title}}) was {{approval.decision}}.\n\n{{approval.comment}}"
    },
    "sla_breach": {
      "name": "SLA breach",
      "subject": "[{{record.priority}}] SLA {{sla.state}} on {{record.code}}",
      "body": "Hi {{user.first_name}},\n\n{{record.code}} \"{{record.name}}\" is {{sla.state}} on its {{sla.kind}} target."
    },
    "renewal_due": {
      "name": "Renewal due",
      "subject": "{{record.name}} renews in {{renewal.days}} days",
      "body": "Hi {{user.first_name}},\n\n{{record.name}} renews on {{renewal.date}}."
    },
    "churn_alert": {
      "name": "Churn alert",
      "subject": "Churn risk: {{record.name}}",
      "body": "Hi {{user.first_name}},\n\n{{record.name}} was flagged at risk: {{risk.trigger}}. Plan a retention action."
    }
  },
  "placeholders": {
    "user.first_name": "Jane",
    "user.name": "Jane Wanjiku",
    "actor.name": "Brian Otieno",
    "owner.name": "Faith Achieng",
    "company.name": "Demulla Investment Ltd",
    "record.type": "Opportunity",
    "record.code": "OPP-2026-0311",
    "record.name": "Savannah Hotels Group — Branch expansion",
    "record.priority": "High",
    "task.code": "TSK-2026-00042",
    "task.title": "Call the finance manager",
    "task.type": "Call",
    "task.due": "21 Oct 2026 10:00",
    "task.priority": "High",
    "stage.from": "Discovery",
    "stage.to": "Demo",
    "stage.days": "18",
    "stage.max_days": "14",
    "outcome.reason": "TIM-01 Not ready – revisit later",
    "escalation.level": "1",
    "escalation.to": "Brian Otieno",
    "escalation.reason": "Overdue 24 hours",
    "approval.title": "15% discount on QTN-2026-01043",
    "approval.reference": "APR-2026-000882",
    "approval.step": "1",
    "approval.decision": "approved",
    "approval.comment": "OK for a 2-year term",
    "requester.name": "Ann Mutua",
    "sla.kind": "resolution",
    "sla.state": "breached",
    "renewal.days": "30",
    "renewal.date": "19 Nov 2026",
    "risk.trigger": "Usage drop (−34% in 30 days)"
  },
  "recipients": {
    "owner": "Owner",
    "manager": "Manager (supervisor)",
    "team_lead": "Team lead",
    "department_head": "Head of department",
    "assigned_by": "Whoever assigned it",
    "requester": "Requester",
    "approver": "Approver",
    "escalated_to": "Escalation target",
    "previous_owner": "Previous owner"
  },
  "channels": {
    "in_app": {
      "label": "In-app",
      "delivered": true
    },
    "email": {
      "label": "Email",
      "delivered": true
    },
    "sms": {
      "label": "SMS",
      "delivered": false
    },
    "whatsapp": {
      "label": "WhatsApp",
      "delivered": false
    }
  },
  "general": {
    "quiet_hours": {
      "enabled": false,
      "from": "20:00",
      "to": "07:00"
    },
    "digest_at": "07:30",
    "timezone": "Africa/Nairobi"
  }
};

const own = { events: {}, templates: {} };
const general = JSON.parse(JSON.stringify(CATALOGUE.general));
const escalation = {
  enabled: true,
  steps: [
    { level: 1, after_hours: 24, to: "supervisor" },
    { level: 2, after_hours: 48, to: "team_lead" },
    { level: 3, after_hours: 72, to: "department_head" },
  ],
};
const TARGETS = { supervisor: "Their supervisor", team_lead: "Their team lead", department_head: "Their head of department", management: "Management (role)" };

const recipientLabel = (r) => CATALOGUE.recipients[r] ?? (r.startsWith("role:") ? `Role: ${roles.find((x) => x.name === r.slice(5))?.label ?? r.slice(5)}` : r);
const channelLabel = (c) => CATALOGUE.channels[c]?.label ?? c;

const event = (key) => {
  const d = CATALOGUE.events[key];
  if (!d) return null;
  const o = own.events[key];
  return {
    key, label: d.label, group: d.group, module: d.module ?? null, template: d.template, tone: d.tone ?? "info", icon: d.icon ?? null,
    notify_actor: !!d.notify_actor,
    channels: [...new Set(["in_app", ...(o?.channels ?? d.channels)])],
    recipients: o?.recipients ?? d.recipients,
    digest: o?.digest ?? false,
    is_active: o?.is_active ?? true,
    customised: !!o,
    defaults: { channels: d.channels, recipients: d.recipients },
  };
};

const template = (key) => {
  const d = CATALOGUE.templates[key];
  if (!d) return null;
  const o = own.templates[key];
  return {
    key, name: d.name, subject: o?.subject ?? d.subject, body: o?.body ?? d.body, customised: !!o,
    default: { subject: d.subject, body: d.body },
    used_by: Object.entries(CATALOGUE.events).filter(([, e]) => e.template === key).map(([k]) => k),
  };
};

const render = (text, vars) =>
  String(text ?? "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, f) => vars[f] ?? "").replace(/^[ \t]+$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
const unknown = (text) => [...new Set([...String(text ?? "").matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]).filter((f) => !(f in CATALOGUE.placeholders)))];

const needReason = (body) => (!String(body.reason ?? "").trim() ? invalid({ reason: ["Give a reason for the change; it goes into the change history."] }) : null);

const overview = () => ({
  events: Object.keys(CATALOGUE.events).map(event),
  templates: Object.keys(CATALOGUE.templates).map(template),
  channels: Object.entries(CATALOGUE.channels).map(([key, c]) => ({ key, ...c })),
  recipients: [
    ...Object.entries(CATALOGUE.recipients).map(([key, label]) => ({ key, label })),
    ...roles.filter((r) => r.is_active).map((r) => ({ key: `role:${r.name}`, label: `Role: ${r.label}` })),
  ],
  placeholders: CATALOGUE.placeholders,
  general,
  escalation: { ...escalation, targets: Object.entries(TARGETS).map(([key, label]) => ({ key, label })) },
});

const ladderWords = (l) => (l.enabled ? l.steps.map((s) => `after ${s.after_hours}h → ${TARGETS[s.to] ?? s.to}`).join(", then ") : "Off");

export default [
  ["get", "tenant/notification-settings", () => ok(overview())],

  ["put", "tenant/notification-settings/events/:key", ({ params, body }) => {
    const before = event(params.key);
    if (!before) return notFound(`Notification event ${params.key} was not found.`);
    const missing = needReason(body);
    if (missing) return missing;
    const next = {
      channels: [...new Set(["in_app", ...(body.channels ?? before.channels)])],
      recipients: body.recipients ?? before.recipients,
      digest: body.digest ?? before.digest,
      is_active: body.is_active ?? before.is_active,
    };
    if (!next.recipients.length) return invalid({ recipients: ["An event needs at least one recipient. Turn it off instead."] });
    own.events[params.key] = next;
    const words = (e) => `${e.is_active ? "" : "Off · "}${e.channels.map(channelLabel).join(", ")} → ${e.recipients.map(recipientLabel).join(", ")}${e.digest ? " (digest)" : ""}`;
    recordChange("notifications", `Notification: ${before.label}`, words(before), words(next), body.reason, body.effective_date || undefined);
    return ok(event(params.key), "Saved. It applies to what happens from now on.");
  }],

  ["post", "tenant/notification-settings/events/:key/restore", ({ params, body }) => {
    const before = event(params.key);
    if (!before) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    delete own.events[params.key];
    recordChange("notifications", `Notification: ${before.label}`, "Customised", "Default restored", body.reason, body.effective_date || undefined);
    return ok(event(params.key), "Back to the default.");
  }],

  ["put", "tenant/notification-settings/templates/:key", ({ params, body }) => {
    const before = template(params.key);
    if (!before) return notFound(`Email template ${params.key} was not found.`);
    const missing = needReason(body);
    if (missing) return missing;
    const bad = unknown(`${body.subject} ${body.body}`);
    if (bad.length) return invalid({ body: [`Unknown merge field(s): ${bad.map((f) => `{{${f}}}`).join(", ")}.`] });
    own.templates[params.key] = { subject: body.subject, body: body.body };
    recordChange("notifications", `Email template: ${before.name}`, before.subject, body.subject, body.reason, body.effective_date || undefined);
    return ok(template(params.key), "Template saved.");
  }],

  ["post", "tenant/notification-settings/templates/:key/restore", ({ params, body }) => {
    const before = template(params.key);
    if (!before) return notFound();
    const missing = needReason(body);
    if (missing) return missing;
    delete own.templates[params.key];
    recordChange("notifications", `Email template: ${before.name}`, "Customised", "Default wording restored", body.reason, body.effective_date || undefined);
    return ok(template(params.key), "Default wording restored.");
  }],

  ["post", "tenant/notification-settings/templates/:key/preview", ({ params, body }) => {
    const t = template(params.key);
    if (!t) return notFound();
    const subject = body.subject ?? t.subject;
    const text = body.body ?? t.body;
    return ok({ subject: render(subject, CATALOGUE.placeholders), body: render(text, CATALOGUE.placeholders), unknown: unknown(`${subject} ${text}`) });
  }],

  ["put", "tenant/notification-settings/general", ({ body }) => {
    const missing = needReason(body);
    if (missing) return missing;
    if (body.quiet_hours) {
      const was = general.quiet_hours.enabled ? `${general.quiet_hours.from}–${general.quiet_hours.to}` : "Off";
      Object.assign(general.quiet_hours, body.quiet_hours);
      const now = general.quiet_hours.enabled ? `${general.quiet_hours.from}–${general.quiet_hours.to}` : "Off";
      if (was !== now) recordChange("notifications", "Quiet hours", was, now, body.reason, body.effective_date || undefined);
    }
    if (body.digest_at && body.digest_at !== general.digest_at) {
      recordChange("notifications", "Daily digest time", general.digest_at, body.digest_at, body.reason, body.effective_date || undefined);
      general.digest_at = body.digest_at;
    }
    if (body.escalation) {
      const was = ladderWords(escalation);
      const steps = (body.escalation.steps ?? escalation.steps).map((s, i) => ({ level: i + 1, after_hours: Number(s.after_hours), to: s.to }));
      const hours = steps.map((s) => s.after_hours);
      if (hours.some((h, i) => i && h <= hours[i - 1])) return invalid({ "escalation.steps": ["Each level must come later than the one before it."] });
      Object.assign(escalation, { enabled: body.escalation.enabled ?? escalation.enabled, steps });
      const now = ladderWords(escalation);
      if (was !== now) recordChange("notifications", "Overdue task escalation", was, now, body.reason, body.effective_date || undefined);
    }
    return ok({ general, escalation }, "Saved.");
  }],

  ["post", "tenant/notification-settings/test", () => ok({ queued: 1 }, "Sent to jane.wanjiku@demulla.co.ke and your in-app notices.")],
];

